import { FeatureType, SettingStructure, SettingsChangeEvent } from '../../../types';
import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';

import { StateController } from '../../../helpers/state/state-management/state-controller';
import { detectMobileViewport } from '../../../helpers/device';
import featuresInfo from '../../../helpers/state/features-info';
import { isDefined } from '../../../helpers/utils';
import topicData from '../../../helpers/state/topic-data';

@customElement('point-approval')
export class PointApproval extends LitElement {
    @query('#approvalRequired')
    approvalRequiredElement?: HTMLInputElement;

    @query('#sendEmail')
    sendEmailElement?: HTMLInputElement;

    @property({type: Boolean})
    isMobile: boolean = false;

    @state()
    approvalElementDetails: SettingStructure =  {
            fieldType: 'boolean',
            inputType: 'toggle',
            title: 'Require Admin Approval on New Points',
            fieldName: 'requires_approval',
            description: 'Require Admin Approval on New Points',
            initialValueBoolean: topicData.topic?.requires_approval ?? false,
            isTopic: true,
            hasEntitlement: featuresInfo.requireApprovalOnPointsEnabled,
            featureType: FeatureType.RequireApprovalOnPoints,
            isSubfield: false,
            isHidden: false,
            flippedLanguage: false,
        };

    @state()
    emailSendDetails: SettingStructure =  {
            fieldType: 'boolean',
            inputType: 'toggle',
            title: 'Send Email to Contributor',
            fieldName: 'send_crowdsource_email',
            description: 'After a contributor submits a point, they will receive an automated email with a link edit or delete just their point at any time. You can disable this email here.',
            initialValueBoolean: topicData.topic?.send_crowdsource_email ?? true,
            isTopic: true,
            hasEntitlement: featuresInfo.customizeAddPointFormEnabled,
            featureType: FeatureType.CustomizeAddPointForm,
            isSubfield: false,
            isHidden: false,
            flippedLanguage: false,
        };

	private readonly bindTopicState = new StateController(this, topicData);

    startup() {
        this.isMobile = detectMobileViewport();
    }


    firstUpdated() {
        if(isDefined(this.approvalRequiredElement)) {
            this.approvalRequiredElement.checked = topicData.topic?.requires_approval ?? false;
        }
        if(isDefined(this.sendEmailElement)) {
            this.sendEmailElement.checked = topicData.topic?.send_crowdsource_email ?? true;
        }
    }

    async hasChange(e: CustomEvent<SettingsChangeEvent>) {
        // dont propigate the event
        e.stopPropagation();
        const newUpdatedValue = e.detail.updatedValue;
        const fieldToUpdate = e.detail.field;

        await topicData.fetchAndUpdateTopicData({
            [fieldToUpdate]: newUpdatedValue,
        }, 'point approval');
    }

    openUpgradeDrawer(e: CustomEvent) {
        this.dispatchEvent(
            new CustomEvent('open-upgrade-dialog', {
                detail: {
                    featureType: e.detail.featureType
                },
            })
        );
    }

    render() {
        return html`
            <setting-component
                style = 'width:${this.approvalElementDetails.widthPercentage ?? 100}%'
                .setting_details=${this.approvalElementDetails}
                .is_hidden=${this.approvalElementDetails.isHidden ?? false}
                @on-change-value=${this.hasChange}
                @open-upgrade-dialog=${(e: CustomEvent) => this.openUpgradeDrawer(e)}
                >
            </setting-component>
            <setting-component
                style = 'width:${this.emailSendDetails.widthPercentage ?? 100}%'
                .setting_details=${this.emailSendDetails}
                .is_hidden=${this.emailSendDetails.isHidden ?? false}
                @on-change-value=${this.hasChange}
                @open-upgrade-dialog=${(e: CustomEvent) => this.openUpgradeDrawer(e)}
                >
            </setting-component>
		`;
    }

    static get styles() {
        return css`
            .setting-switch {
                display: grid;
                grid-auto-flow: column;
                grid-auto-columns: 1fr;
                align-items: center;
                justify-content: space-evenly;
            }

            .switch-label {
                text-align: center;
            }

            .switch-icon {
                margin-left: auto;
                margin-right: auto;
            }

            .helpSubHeader {
                margin-bottom: 10px;
                font-size: 0.75rem;
                color: var(--main-primary-color);
            }
        `;
    }
}
